import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Copy, Plus, Printer, Save, Tags, Trash2 } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router"

import { api } from "@/api/cliente"
import type { Empresa } from "@/componentes/empresa/tipos"
import { COMPANIAS, type Compania, PAPELES } from "@/componentes/identidad/identidad"
import { cabenPorHoja } from "@/componentes/impresos/hoja"
import { PliegoImpresion } from "@/componentes/impresos/PliegoImpresion"
import { EditorRotulo } from "@/componentes/rotulacion/EditorRotulo"
import { ALTO_HOJA, MEDIDAS } from "@/componentes/rotulacion/medidas"
import type { DatosRotulo, Precarga, Rotulo, TipoRotulo } from "@/componentes/rotulacion/tipos"
import { rotuloEnBlanco } from "@/componentes/rotulacion/tipos"
import { VistaRotulo } from "@/componentes/rotulacion/VistaRotulo"
import { Boton } from "@/componentes/ui/Boton"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFechaHora } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/**
 * Rotulación de carpetas: lo que se pega por fuera del archivo físico.
 *
 * Sustituye a `Etiquetas, caratulas, etc de carpetas.xls`. La diferencia que importa
 * no es que esté en el sistema: es que **la medida de lo impreso es un dato y no una
 * consecuencia**. En el `.xls` una etiqueta mide lo que midan sus columnas
 * fusionadas, y por eso cambia de tamaño según el equipo, la fuente por defecto y
 * quién ensanchó la última columna para que le cupiera un texto. Ver `medidas.ts`.
 *
 * ⚠ **La vista previa es el MISMO componente que se imprime, no una aproximación.**
 * `VistaRotulo` pinta la pantalla y `PliegoImpresion` pinta el papel llamándolo. Una
 * previa dibujada aparte es una segunda verdad que se desincroniza en el primer
 * ajuste, y en un módulo cuyo único fin es ver antes de gastar papel, eso lo anula.
 *
 * ⚠ **Se imprime lo que hay en el FORMULARIO, esté guardado o no.** Es lo contrario
 * de lo que hace la firma de correo, y a propósito: allí la tarjeta se manda a
 * clientes y debe coincidir con la ficha; aquí se gasta una hoja y se pega en una
 * carpeta. Obligar a guardar antes de probar una impresión convertiría la bandeja en
 * un cajón de borradores a medio hacer.
 */
export function Rotulacion() {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeEditar = tienePermiso(usuario, "rotulo:editar")

  const [tipo, setTipo] = useState<TipoRotulo>("PESTANA")
  const [datos, setDatos] = useState<DatosRotulo>(() => rotuloEnBlanco("PESTANA"))
  const [titulo, setTitulo] = useState("")
  const [editando, setEditando] = useState<number | null>(null)
  const [copias, setCopias] = useState(1)
  const [fallo, setFallo] = useState<string | null>(null)
  /** La cartulina de la vista previa de la carátula. No se guarda ni se imprime. */
  const [papel, setPapel] = useState<string>(PAPELES[0].color)

  const empresa = useQuery({
    queryKey: ["empresa"],
    queryFn: () => api.get<Empresa>("/api/v1/empresa"),
  })

  const guardados = useQuery({
    queryKey: ["rotulos", tipo],
    queryFn: () => api.get<Rotulo[]>(`/api/v1/rotulos?tipo=${tipo}`),
  })

  /** Cambiar de formato reinicia el formulario: los campos no se parecen entre sí. */
  function cambiarTipo(nuevo: TipoRotulo) {
    setTipo(nuevo)
    setDatos({ ...rotuloEnBlanco(nuevo), compania: datos.compania } as DatosRotulo)
    setEditando(null)
    setTitulo("")
    setFallo(null)
  }

  function cargar(rotulo: Rotulo) {
    setTipo(rotulo.tipo)
    // ⚠ Lo guardado antes del manual no trae compañía ni los campos nuevos: se completan
    // con los del rótulo en blanco, o el formulario quedaría con campos sin controlar.
    setDatos({ ...rotuloEnBlanco(rotulo.tipo), ...rotulo.datos } as DatosRotulo)
    setTitulo(rotulo.titulo)
    setEditando(rotulo.id)
    setFallo(null)
  }

  /**
   * Trae lo que el sistema sabe del cliente o del expediente.
   *
   * ⚠ **Rellena sólo los campos VACÍOS.** Pisar lo escrito castigaría a quien ya
   * había tecleado media etiqueta antes de acordarse de enlazar el expediente —y ése
   * es justo el orden en que la gente lo hace.
   */
  const precargar = useMutation({
    mutationFn: (busca: string) => api.get<Precarga>(`/api/v1/rotulos/precarga?${busca}`),
    onSuccess: (sugerencias) => {
      setDatos((actual) => {
        const conservar = <T,>(valor: T, sugerido: T | undefined): T =>
          valor ? valor : ((sugerido ?? valor) as T)
        switch (actual.tipo) {
          case "PESTANA":
            return {
              ...actual,
              contenido: conservar(actual.contenido, sugerencias.nombre_expediente),
              siglas: conservar(actual.siglas, sugerencias.siglas ?? null),
            }
          case "ETIQUETA_CARPETA":
            return {
              ...actual,
              empresa: conservar(actual.empresa, sugerencias.empresa),
              siglas: conservar(actual.siglas, sugerencias.siglas),
              nombre_expediente: conservar(actual.nombre_expediente, sugerencias.nombre_expediente),
            }
          case "CARATULA":
            return {
              ...actual,
              entidad: conservar(actual.entidad, sugerencias.entidad ?? null),
              rif: conservar(actual.rif, sugerencias.rif ?? null),
              titulo: conservar(actual.titulo, sugerencias.nombre_expediente),
            }
          case "TEJUELO":
            return {
              ...actual,
              empresa: conservar(actual.empresa, sugerencias.empresa),
              rif: conservar(actual.rif, sugerencias.rif ?? null),
            }
          case "LOMO":
            return {
              ...actual,
              titulo: conservar(actual.titulo, sugerencias.empresa),
              siglas: conservar(actual.siglas, sugerencias.siglas ?? null),
            }
        }
      })
    },
    onError: (e: Error) => setFallo(e.message),
  })

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = { titulo, datos, cliente_id: null, expediente_id: null }
      return editando === null
        ? api.post<Rotulo>("/api/v1/rotulos", cuerpo)
        : api.patch<Rotulo>(`/api/v1/rotulos/${editando}`, { titulo, datos })
    },
    onSuccess: (rotulo) => {
      clienteQuery.invalidateQueries({ queryKey: ["rotulos"] })
      setEditando(rotulo.id)
      setFallo(null)
    },
    onError: (e: Error) => setFallo(e.message),
  })

  const duplicar = useMutation({
    mutationFn: (id: number) => api.post<Rotulo>(`/api/v1/rotulos/${id}/duplicar`),
    onSuccess: (copia) => {
      clienteQuery.invalidateQueries({ queryKey: ["rotulos"] })
      cargar(copia)
    },
    onError: (e: Error) => setFallo(e.message),
  })

  const borrar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/v1/rotulos/${id}`),
    onSuccess: (_, id) => {
      clienteQuery.invalidateQueries({ queryKey: ["rotulos"] })
      if (editando === id) {
        setEditando(null)
        setTitulo("")
        setDatos(rotuloEnBlanco(tipo))
      }
    },
    onError: (e: Error) => setFallo(e.message),
  })

  const medida = MEDIDAS[tipo]
  // ⚠ El lomo toma su ANCHO del rótulo —es el grosor del canto—, no del formato; el
  // alto sí es el del formato. Los demás, los dos del formato. Antes esto cruzaba los
  // ejes porque la tira se dibujaba acostada; de pie ya no hay nada que cruzar.
  const anchoReal = datos.tipo === "LOMO" ? datos.ancho_mm : medida.ancho
  const altoReal =
    datos.tipo === "LOMO"
      ? datos.alto_mm
      : datos.tipo === "CARATULA"
        ? ALTO_HOJA[datos.hoja]
        : medida.alto
  // ⚠ La carátula ES la hoja: una por página, sin guías de corte y con su `@page` a sangre.
  const esCaratula = datos.tipo === "CARATULA"
  const porHoja = esCaratula ? 1 : cabenPorHoja(anchoReal, altoReal)
  const paginaDeLaRuta = !esCaratula
    ? undefined
    : datos.hoja === "OFICIO"
      ? "ruta-rotulo-oficio"
      : "ruta-rotulo-carta"

  return (
    <div className={paginaDeLaRuta}>
      <header className="no-print mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Tags size={20} aria-hidden={true} />
            Rotulación de carpetas
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            Pestañas, lomos, carátulas, tapas y tejuelos, a medida en milímetros. Lo que ves aquí es
            exactamente lo que sale de la impresora.
          </p>
        </div>
        <Link to="/identidad" className="boton-secundario h-9 px-3 text-sm">
          Ver el manual de identidad
        </Link>
      </header>

      {/* La compañía va primero: es igual en los cinco formatos (minuta del 30/10). */}
      <fieldset className="no-print mb-4 flex flex-wrap items-center gap-2 border-0 p-0">
        <legend className="mb-2 text-xs font-medium">Compañía</legend>
        {(Object.keys(COMPANIAS) as Compania[]).map((clave) => (
          <button
            key={clave}
            type="button"
            onClick={() => setDatos({ ...datos, compania: clave } as DatosRotulo)}
            aria-pressed={(datos.compania ?? "SETIECA_AGENTES") === clave}
            className={
              (datos.compania ?? "SETIECA_AGENTES") === clave
                ? "boton-primario h-9 px-3 text-sm"
                : "boton-secundario h-9 px-3 text-sm"
            }
          >
            {COMPANIAS[clave].selector}
          </button>
        ))}
      </fieldset>
      {datos.compania === "SETIECA_TRADE" ? (
        <p className="no-print mb-4 text-sm" style={{ color: "var(--warning)" }}>
          Falta el logo vectorial de Setieca Trade: mientras llega, el rótulo lleva su nombre en
          texto.
        </p>
      ) : null}

      {/* Selector de formato. */}
      <div className="no-print mb-6 flex flex-wrap gap-2">
        {(Object.keys(MEDIDAS) as TipoRotulo[]).map((clave) => (
          <button
            key={clave}
            type="button"
            onClick={() => cambiarTipo(clave)}
            aria-pressed={tipo === clave}
            className={
              tipo === clave
                ? "boton-primario h-9 px-3 text-sm"
                : "boton-secundario h-9 px-3 text-sm"
            }
          >
            {MEDIDAS[clave].etiqueta}
          </button>
        ))}
      </div>

      <div className="no-print grid gap-6 lg:grid-cols-[minmax(0,26rem)_1fr]">
        {/* ── Columna izquierda: el formulario ── */}
        <section className="grid gap-4">
          <div className="superficie p-4">
            <h2 className="rotulo mb-3">Datos</h2>
            <EditorRotulo datos={datos} onCambio={setDatos} />
          </div>

          <div className="superficie p-4">
            <h2 className="rotulo mb-3">Precargar del sistema</h2>
            <p className="mb-2 text-xs" style={{ color: "var(--fg-muted)" }}>
              Trae la razón social, el RIF y las siglas. Sólo rellena lo que esté vacío.
            </p>
            <div className="flex gap-2">
              <input
                className="campo h-9 flex-1 text-sm"
                placeholder="N.° de cliente"
                inputMode="numeric"
                aria-label="Número de cliente para precargar"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.currentTarget.value) {
                    precargar.mutate(`cliente_id=${e.currentTarget.value}`)
                  }
                }}
              />
              <input
                className="campo h-9 flex-1 text-sm"
                placeholder="N.° de expediente"
                inputMode="numeric"
                aria-label="Número de expediente para precargar"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.currentTarget.value) {
                    precargar.mutate(`expediente_id=${e.currentTarget.value}`)
                  }
                }}
              />
            </div>
            <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
              Pulsa Intro en el campo que uses.
            </p>
          </div>

          {puedeEditar && (
            <div className="superficie p-4">
              <h2 className="rotulo mb-3">Guardar para reimprimir</h2>
              <input
                className="campo mb-2 h-9 w-full text-sm"
                placeholder="Con qué nombre lo buscarás después"
                aria-label="Título del rótulo guardado"
                value={titulo}
                maxLength={160}
                onChange={(e) => setTitulo(e.target.value)}
              />
              <Boton
                variante="primario"
                disabled={titulo.trim() === "" || guardar.isPending}
                onClick={() => guardar.mutate()}
              >
                <Save size={15} aria-hidden={true} />
                {editando === null ? "Guardar" : "Guardar cambios"}
              </Boton>
              {editando !== null && (
                <Boton
                  className="ml-2"
                  onClick={() => {
                    setEditando(null)
                    setTitulo("")
                    setDatos(rotuloEnBlanco(tipo))
                  }}
                >
                  <Plus size={15} aria-hidden={true} />
                  Empezar otro
                </Boton>
              )}
            </div>
          )}

          {fallo !== null && (
            <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
              {fallo}
            </p>
          )}
        </section>

        {/* ── Columna derecha: la previa y la impresión ── */}
        <section className="grid gap-4">
          <div className="superficie p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 className="rotulo flex-1">Vista previa</h2>
              {esCaratula ? (
                <label className="flex items-center gap-2 text-xs">
                  Papel de la vista previa
                  <select
                    className="campo h-8 text-xs"
                    value={papel}
                    onChange={(e) => setPapel(e.target.value)}
                  >
                    {PAPELES.map((p) => (
                      <option key={p.etiqueta} value={p.color}>
                        {p.etiqueta}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <span className="tabular text-xs" style={{ color: "var(--fg-muted)" }}>
                {anchoReal} × {altoReal} mm · {porHoja} por hoja
              </span>
            </div>

            {/* ⚠ Se muestra a escala real (1 mm = 1 mm) y con desplazamiento si no
                cabe, en vez de encogerlo para que quepa: el módulo existe para que
                lo que se ve sea lo que se imprime, y una previa reducida deja de
                serlo justo en lo único que importa. */}
            <div
              className="overflow-auto"
              style={{ background: "var(--surface-elevated)", padding: "1rem" }}
            >
              <div className="pieza-impresa-previa" style={{ width: "fit-content" }}>
                <VistaRotulo
                  datos={datos}
                  empresa={empresa.data}
                  papel={esCaratula ? papel : undefined}
                />
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label className="grid gap-1">
                <span className="text-xs font-medium">Copias</span>
                <input
                  type="number"
                  min={1}
                  max={100}
                  className="campo tabular h-9 w-24 text-sm"
                  value={copias}
                  onChange={(e) =>
                    setCopias(Math.min(100, Math.max(1, Number(e.target.value) || 1)))
                  }
                />
              </label>
              <Boton variante="primario" onClick={() => window.print()}>
                <Printer size={15} aria-hidden={true} />
                Imprimir
              </Boton>
              <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                En el diálogo, deja la escala en <strong>100 %</strong> y activa los gráficos de
                fondo. Para el PDF vectorial, elige «Guardar como PDF» como destino.
              </p>
            </div>
          </div>

          {/* La bandeja de guardados. */}
          <div className="superficie p-4">
            <h2 className="rotulo mb-3">Guardados · {medida.etiqueta}</h2>
            {guardados.data === undefined ? (
              <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                Cargando…
              </p>
            ) : guardados.data.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                Todavía no hay ninguno de este formato. Los que guardes aparecen aquí para
                reimprimirlos sin volver a teclearlos.
              </p>
            ) : (
              <ul className="grid gap-2">
                {guardados.data.map((rotulo) => (
                  <li
                    key={rotulo.id}
                    className="flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] border p-2"
                    style={{ borderColor: "var(--border)" }}
                  >
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => cargar(rotulo)}
                    >
                      <span className="block truncate text-sm font-medium">{rotulo.titulo}</span>
                      <span className="tabular block text-xs" style={{ color: "var(--fg-muted)" }}>
                        {comoFechaHora(rotulo.actualizado_en)}
                        {rotulo.cliente_nombre ? ` · ${rotulo.cliente_nombre}` : ""}
                      </span>
                    </button>
                    {puedeEditar && (
                      <>
                        <button
                          type="button"
                          className="boton-secundario h-8 px-2 text-xs"
                          aria-label={`Duplicar «${rotulo.titulo}»`}
                          onClick={() => duplicar.mutate(rotulo.id)}
                        >
                          <Copy size={14} aria-hidden={true} />
                        </button>
                        <button
                          type="button"
                          className="boton-secundario h-8 px-2 text-xs"
                          aria-label={`Eliminar «${rotulo.titulo}»`}
                          onClick={() => borrar.mutate(rotulo.id)}
                        >
                          <Trash2 size={14} aria-hidden={true} />
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {/* Fuera del flujo en pantalla; es lo único que se ve en el papel. */}
      <PliegoImpresion
        pieza={<VistaRotulo datos={datos} empresa={empresa.data} />}
        copias={copias}
        porHoja={porHoja}
        guias={!esCaratula}
      />
    </div>
  )
}
